import { Task, UserSettings, FilterSettings } from '../types';

// LocalStorageのキー定義
const STORAGE_KEYS = {
  TASKS: 'wedding-task-planner-tasks',
  USER_SETTINGS: 'wedding-task-planner-user-settings',
  FILTER_SETTINGS: 'wedding-task-planner-filter-settings',
  APP_VERSION: 'wedding-task-planner-version'
} as const;

// アプリケーションバージョン（データ移行用）
const APP_VERSION = '1.0.0';

// LocalStorageヘルパークラス
export class StorageService {
  // タスクデータの保存
  static saveTasks(tasks: Task[]): boolean {
    try {
      const data = {
        tasks,
        version: APP_VERSION,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('タスクデータの保存に失敗しました:', error);
      return false;
    }
  }

  // タスクデータの読み込み
  static loadTasks(): Task[] | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.TASKS);
      if (!stored) return null;

      const data = JSON.parse(stored);
      
      // バージョンチェック（将来のマイグレーション用）
      if (data.version !== APP_VERSION) {
        console.warn('データバージョンが異なります。移行処理が必要な場合があります。');
      }

      return Array.isArray(data.tasks) ? data.tasks : null;
    } catch (error) {
      console.error('タスクデータの読み込みに失敗しました:', error);
      return null;
    }
  }

  // ユーザー設定の保存
  static saveUserSettings(settings: UserSettings): boolean {
    try {
      const data = {
        settings,
        version: APP_VERSION,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEYS.USER_SETTINGS, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('ユーザー設定の保存に失敗しました:', error);
      return false;
    }
  }

  // ユーザー設定の読み込み
  static loadUserSettings(): UserSettings | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.USER_SETTINGS);
      if (!stored) return null;

      const data = JSON.parse(stored);
      return data.settings || null;
    } catch (error) {
      console.error('ユーザー設定の読み込みに失敗しました:', error);
      return null;
    }
  }

  // フィルター設定の保存
  static saveFilterSettings(settings: FilterSettings): boolean {
    try {
      const data = {
        settings,
        version: APP_VERSION,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEYS.FILTER_SETTINGS, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('フィルター設定の保存に失敗しました:', error);
      return false;
    }
  }

  // フィルター設定の読み込み
  static loadFilterSettings(): FilterSettings | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.FILTER_SETTINGS);
      if (!stored) return null;

      const data = JSON.parse(stored);
      return data.settings || null;
    } catch (error) {
      console.error('フィルター設定の読み込みに失敗しました:', error);
      return null;
    }
  }

  // 全データのクリア
  static clearAllData(): boolean {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.error('データのクリアに失敗しました:', error);
      return false;
    }
  }

  // データのエクスポート（バックアップ）
  static exportData(): string | null {
    try {
      const exportData = {
        tasks: this.loadTasks(),
        userSettings: this.loadUserSettings(),
        filterSettings: this.loadFilterSettings(),
        exportedAt: new Date().toISOString(),
        version: APP_VERSION
      };
      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      console.error('データのエクスポートに失敗しました:', error);
      return null;
    }
  }

  // データのインポート（復元）
  static importData(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      
      if (data.tasks) {
        this.saveTasks(data.tasks);
      }
      if (data.userSettings) {
        this.saveUserSettings(data.userSettings);
      }
      if (data.filterSettings) {
        this.saveFilterSettings(data.filterSettings);
      }
      
      return true;
    } catch (error) {
      console.error('データのインポートに失敗しました:', error);
      return false;
    }
  }

  // ストレージサイズの確認
  static getStorageInfo(): { used: number; quota: number; percentage: number } {
    try {
      let used = 0;
      Object.values(STORAGE_KEYS).forEach(key => {
        const item = localStorage.getItem(key);
        if (item) {
          used += item.length;
        }
      });

      // LocalStorageの概算制限（ブラウザによって異なる）
      const quota = 5 * 1024 * 1024; // 5MB
      const percentage = (used / quota) * 100;

      return { used, quota, percentage };
    } catch (error) {
      console.error('ストレージ情報の取得に失敗しました:', error);
      return { used: 0, quota: 0, percentage: 0 };
    }
  }
} 